import { QuestionOutlineIcon, StarIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { Button, IconButton } from '@revelio/ui';

import { getTextAssist } from '../../../api/build-a-dash-ai-gen-api';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getAvailableMetrics } from '../../ChartConfig/utils/metric-mapping';
import type { MultiMetricDataMap } from '../../DataProvider/MultiMetricProvider';
import { TextMultiMetricProvider } from '../../DataProvider/TextMultiMetricProvider';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import type { UpdateChartContentParams } from '../../TextChartContentProvider/TextChartContentProvider';
import { ChartError } from '../ChartError';
import { ChartLoader } from '../ChartLoader';
import { ChartProvider } from '../ChartProvider';
import { ChartMenuDataProvider } from '../chart-menu/ChartMenuContext';
import { processMultiMetricData } from '../utils/process-multi-metric-data';
import { TextContentEditorModal } from './TextContentEditorModal';
import { TextRenderer } from './TextRenderer';

type TextContentEditorProps = {
  item: PositionedItem;
  value: string;
  onChange: (value: string) => void;
  primaryFilterId: PrimaryFilterId;
};

type TextPreviewProps = {
  text: string;
  item: PositionedItem;
  primaryFilterId: PrimaryFilterId;
  onMultiDataChange?: (multiData: MultiMetricDataMap) => void;
};

type TextPreviewInnerProps = {
  text: string;
  item: PositionedItem;
  primaryFilterId: PrimaryFilterId;
  onMultiDataChange?: (multiData: MultiMetricDataMap) => void;
};

function SyncMultiDataForValidation({
  multiData,
  onMultiDataChange,
}: {
  multiData: MultiMetricDataMap;
  onMultiDataChange?: (multiData: MultiMetricDataMap) => void;
}) {
  useEffect(() => {
    onMultiDataChange?.(multiData);
  }, [multiData, onMultiDataChange]);
  return null;
}

const TextPreviewInner = ({
  text,
  item,
  primaryFilterId,
  onMultiDataChange,
}: TextPreviewInnerProps) => {
  const { config: pageConfig } = usePageConfig();
  const { activeFilterMap } = useFilterContext();
  const handleProcessedContent = useCallback(
    (_params: UpdateChartContentParams) => undefined,
    []
  );

  return (
    <ChartMenuDataProvider>
      <TextMultiMetricProvider primaryDimension={primaryFilterId} text={text}>
        {({ data, status }) => {
          if (status === DataProviderQueryStatus.Idle) {
            return <ChartLoader />;
          }

          if (status === DataProviderQueryStatus.Fetching) {
            return <ChartLoader />;
          }

          if (status === DataProviderQueryStatus.Error) return <ChartError />;

          if (!text) return null;

          const byMetric = data?.byMetric;
          const multiData: MultiMetricDataMap | undefined = byMetric
            ? processMultiMetricData({
                byMetric,
                plotParameters: item?.plot?.plotParameters ?? [],
                reportParameters:
                  pageConfig.tabs?.[0]?.tabContent?.reportParameters ?? [],
                activeFilterMap,
              })
            : byMetric;

          return (
            <>
              {multiData ? (
                <SyncMultiDataForValidation
                  multiData={multiData}
                  onMultiDataChange={onMultiDataChange}
                />
              ) : null}
              <TextRenderer
                content={text}
                multiData={multiData}
                color={item.plot?.color}
                item={item}
                onProcessedContent={handleProcessedContent}
              />
            </>
          );
        }}
      </TextMultiMetricProvider>
    </ChartMenuDataProvider>
  );
};

const TextPreview = ({
  text,
  item,
  primaryFilterId,
  onMultiDataChange,
}: TextPreviewProps) => {
  return (
    <TextPreviewInner
      text={text}
      item={{
        ...item,
        plot: { ...item.plot, textHtml: text },
      }}
      primaryFilterId={primaryFilterId}
      onMultiDataChange={onMultiDataChange}
    />
  );
};

type EditorControlsProps = {
  text: string;
  onTextChange: (text: string) => void;
  primaryFilterId: PrimaryFilterId;
};

const EditorControls = ({
  text,
  onTextChange,
  primaryFilterId,
}: EditorControlsProps) => {
  const queryClient = useQueryClient();
  const [aiPrompt, setAiPrompt] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const availableGroupedMetrics = getAvailableMetrics({
    primaryFilterId,
    chartType: 'text',
  });

  const availableMetricHints: Array<{ value: string; description: string }> =
    [];

  for (const groupedMetric of availableGroupedMetrics) {
    const groupLabel = groupedMetric.label;
    const options = groupedMetric.options;
    for (const opt of options) {
      const optValue = opt.value;
      const description = groupLabel
        ? `${optValue} for ${groupLabel}`
        : optValue;
      availableMetricHints.push({ value: optValue, description });
    }
  }

  const textAssistQueryKey = ['text-assist-modal', primaryFilterId];
  const {
    isFetching: isAiFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: textAssistQueryKey,
    queryFn: async ({ signal }) => {
      return await getTextAssist(
        { template: text, prompt: aiPrompt, metrics: availableMetricHints },
        { signal }
      );
    },
    enabled: false,
    retry: false,
  });

  const handleAiAssist = async () => {
    try {
      const result = await refetch();
      const nextTemplate = result.data?.template;
      if (nextTemplate) {
        onTextChange(nextTemplate);
        setIsPopoverOpen(false);
      }
    } catch {
      // If aborted/canceled, ignore.
    }
  };

  const handleAbortAiAssist = async () => {
    await queryClient.cancelQueries({ queryKey: textAssistQueryKey });
  };

  return (
    <HStack spacing={2}>
      <Tooltip label="Template syntax help" fontSize="xs">
        <IconButton
          aria-label="Template syntax help"
          icon={<QuestionOutlineIcon />}
          size="sm"
          variant="outlined"
          onClick={() => {
            window.open(
              '/help/text-chart',
              '_blank',
              'width=900,height=800,scrollbars=yes'
            );
          }}
        />
      </Tooltip>
      <Popover
        placement="bottom-end"
        isLazy
        isOpen={isPopoverOpen}
        onClose={() => setIsPopoverOpen(false)}
        onOpen={() => setIsPopoverOpen(true)}
      >
        <PopoverTrigger>
          <IconButton
            aria-label="AI assist"
            icon={<StarIcon />}
            size="sm"
            variant="outlined"
          />
        </PopoverTrigger>
        <PopoverContent minW="320px" p={0}>
          <Box p={4}>
            <Text fontSize="sm" fontWeight={600} mb={2}>
              AI Assist
            </Text>
            <Text fontSize="xs" color="gray.600" mb={2}>
              Describe what you want.
            </Text>
            <Textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g. rewrite this intro to be more concise"
              rows={3}
              fontSize="sm"
              resize="none"
              mb={2}
              isDisabled={isAiFetching}
            />
            <Flex justifyContent="end" gap={2}>
              {!isAiFetching && (
                <Button
                  size="sm"
                  variant="ghost"
                  color="secondary"
                  onClick={() => setAiPrompt('')}
                  disabled={isAiFetching}
                >
                  Clear
                </Button>
              )}
              {isAiFetching && (
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={handleAbortAiAssist}
                  disabled={!isAiFetching}
                >
                  Abort
                </Button>
              )}
              <Button
                size="sm"
                color="primary"
                onClick={handleAiAssist}
                loading={isAiFetching}
                disabled={isAiFetching}
              >
                Apply
              </Button>
            </Flex>
            {error ? (
              <Text fontSize="xs" color="red.500" mt={2}>
                Failed to generate. Please try again.
              </Text>
            ) : null}
          </Box>
        </PopoverContent>
      </Popover>
    </HStack>
  );
};

export const TextContentEditor = ({
  item,
  value,
  onChange,
  primaryFilterId,
}: TextContentEditorProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [validationMultiData, setValidationMultiData] = useState<
    MultiMetricDataMap | undefined
  >(undefined);
  return (
    <ChartProvider item={item}>
      <TextContentEditorModal
        isOpen={isOpen}
        onClose={onClose}
        startingText={value}
        onSave={onChange}
        validationMultiData={validationMultiData}
        renderPreview={(text) => (
          <TextPreview
            text={text}
            item={item}
            primaryFilterId={primaryFilterId}
            onMultiDataChange={setValidationMultiData}
          />
        )}
        renderControls={(text, onTextChange) => (
          <EditorControls
            text={text}
            onTextChange={onTextChange}
            primaryFilterId={primaryFilterId}
          />
        )}
      />
      <Button fullWidth variant="outlined" onClick={onOpen}>
        Open Content Editor
      </Button>
    </ChartProvider>
  );
};
